import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  Clock,
  Ghost,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  CheckCircle,
  XCircle,
  RefreshCw,
  MoreHorizontal,
  Inbox,
  Search,
  History,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { ConfirmationModal } from './ConfirmationModal'

// Constants
const ITEMS_PER_PAGE = 25

// Types
interface GhostUnit {
  unit_id: number
  unit_ref: string
  typology: string | null
  current_price: number | null
  last_scraped_at: string | null
  days_not_seen: number
  status: string
}

interface MonitoringIssue {
  project_id: number
  project_name: string
  broker_name: string
  website: string
  issue_type: 'scraper_error' | 'stale' | 'ghost_units' | 'delisted_at_source' | 'relisted'
  error_message: string | null
  error_classification: string | null
  days_since_success: number
  consecutive_errors: number
  first_error_at: string | null
  last_successful_scrape: string | null
  total_units: number
  available_units: number
  sold_units: number
  reserved_units: number
  completion_rate: number
  ghost_units: GhostUnit[] | null
  ghost_unit_count: number | null
  days_until_auto_cleanup: number | null
  recommendation: {
    action: string
    confidence: 'high' | 'medium' | 'low'
    reasoning: string
  }
  recommended_sold_date: string | null
  sold_date_reasoning: string | null
}

interface IssuesTableProps {
  issues: MonitoringIssue[] | undefined
  isLoading: boolean
  onRefetch: () => void
}

type IssueFilter = 'all' | 'scraper_error' | 'stale' | 'ghost_units' | 'delisted_at_source' | 'relisted'

type SortField = 'project_name' | 'issue_type' | 'recommendation.confidence' | 'completion_rate' | 'available_units' | 'days_since_success'

interface SortConfig {
  field: SortField
  direction: 'asc' | 'desc'
}

const issueTypeConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  scraper_error: { label: 'Scraper Error', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  stale: { label: 'Stale', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  ghost_units: { label: 'Ghost Units', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Ghost },
  delisted_at_source: { label: 'Delisted', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Inbox },
  relisted: { label: 'Relisted', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: History },
}

const confidenceColors: Record<string, string> = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-secondary-100 text-secondary-700',
}

const confidenceOrder: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
}

const actionLabels: Record<string, string> = {
  mark_sold_out: 'Mark as Sold Out',
  check_url: 'Check URL',
  retry: 'Retry Scrape',
  confirm_sold: 'Confirm Sold',
  dismiss: 'Dismiss',
  review_sold_out: 'Review sold-out flag',
}

function humanizeAction(action: string): string {
  if (actionLabels[action]) return actionLabels[action]
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

interface ResolveAction {
  type: 'mark_sold_out' | 'dismiss' | 'rescrape' | 'confirm_sold'
  projectId: number
  projectName: string
  availableUnits?: number
  totalUnits?: number
  soldUnits?: number
  reservedUnits?: number
  recommendedSoldDate?: string | null
  soldDateReasoning?: string | null
  ghostUnitCount?: number | null
}

export function IssuesTable({ issues, isLoading, onRefetch }: IssuesTableProps) {
  const [filter, setFilter] = useState<IssueFilter>('all')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [modalAction, setModalAction] = useState<ResolveAction | null>(null)
  const [soldDate, setSoldDate] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'days_since_success', direction: 'desc' })
  const [brokerFilter, setBrokerFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [currentPage, setCurrentPage] = useState<number>(1)
  const queryClient = useQueryClient()

  const resolveMutation = useMutation({
    mutationFn: async ({ projectId, body }: { projectId: number; body: Record<string, any> }) => {
      const response = await api.post(`/automation/monitoring/issues/${projectId}/resolve`, body)
      return response.data
    },
    onSuccess: (_data, variables) => {
      toast.success(`Action completed for project #${variables.projectId}`)
      queryClient.invalidateQueries({ queryKey: ['monitoring-issues'] })
      queryClient.invalidateQueries({ queryKey: ['monitoring-health'] })
      queryClient.invalidateQueries({ queryKey: ['monitoring-issues-meta'] })
      onRefetch()
      setModalAction(null)
      setSoldDate('')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to resolve issue')
    },
  })

  const handleAction = (action: ResolveAction) => {
    if (action.type === 'mark_sold_out') {
      setModalAction(action)
      setSoldDate(action.recommendedSoldDate || new Date().toISOString().split('T')[0])
    } else if (action.type === 'confirm_sold') {
      setModalAction(action)
    } else {
      // dismiss and rescrape are direct actions
      resolveMutation.mutate({
        projectId: action.projectId,
        body: { action: action.type },
      })
    }
  }

  const confirmAction = () => {
    if (!modalAction) return

    const body: Record<string, any> = { action: modalAction.type }
    if (modalAction.type === 'mark_sold_out' && soldDate) {
      body.sold_out_date = soldDate
    }

    resolveMutation.mutate({
      projectId: modalAction.projectId,
      body,
    })
  }

  // Extract unique broker names
  const brokerNames = useMemo(() => {
    if (!issues) return []
    const names = Array.from(new Set(issues.map((i) => i.broker_name).filter(Boolean)))
    return names.sort((a, b) => a.localeCompare(b))
  }, [issues])

  // Filtered issues: apply search, broker filter, and issue type filter
  const filteredIssues = useMemo(() => {
    let result = issues ?? []

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      result = result.filter(
        (issue) =>
          issue.project_name.toLowerCase().includes(q) ||
          issue.broker_name.toLowerCase().includes(q)
      )
    }

    // Broker filter
    if (brokerFilter !== 'all') {
      result = result.filter((issue) => issue.broker_name === brokerFilter)
    }

    // Issue type filter
    if (filter !== 'all') {
      result = result.filter((issue) => issue.issue_type === filter)
    }

    return result
  }, [issues, searchQuery, brokerFilter, filter])

  // Filter counts respect search and broker filter (but not issue type filter)
  const filterCounts = useMemo(() => {
    let base = issues ?? []

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      base = base.filter(
        (issue) =>
          issue.project_name.toLowerCase().includes(q) ||
          issue.broker_name.toLowerCase().includes(q)
      )
    }

    // Apply broker filter
    if (brokerFilter !== 'all') {
      base = base.filter((issue) => issue.broker_name === brokerFilter)
    }

    return {
      all: base.length,
      scraper_error: base.filter((i) => i.issue_type === 'scraper_error').length,
      stale: base.filter((i) => i.issue_type === 'stale').length,
      ghost_units: base.filter((i) => i.issue_type === 'ghost_units').length,
      delisted_at_source: base.filter((i) => i.issue_type === 'delisted_at_source').length,
      relisted: base.filter((i) => i.issue_type === 'relisted').length,
    }
  }, [issues, searchQuery, brokerFilter])

  // Sorted issues
  const sortedIssues = useMemo(() => {
    const sorted = [...filteredIssues]
    sorted.sort((a, b) => {
      let comparison = 0

      switch (sortConfig.field) {
        case 'project_name':
          comparison = a.project_name.localeCompare(b.project_name)
          break
        case 'issue_type':
          comparison = a.issue_type.localeCompare(b.issue_type)
          break
        case 'recommendation.confidence':
          comparison = (confidenceOrder[a.recommendation.confidence] ?? 0) - (confidenceOrder[b.recommendation.confidence] ?? 0)
          break
        case 'completion_rate':
          comparison = a.completion_rate - b.completion_rate
          break
        case 'available_units':
          comparison = a.available_units - b.available_units
          break
        case 'days_since_success':
          comparison = a.days_since_success - b.days_since_success
          break
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison
    })
    return sorted
  }, [filteredIssues, sortConfig])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedIssues.length / ITEMS_PER_PAGE))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedIssues = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE
    return sortedIssues.slice(start, start + ITEMS_PER_PAGE)
  }, [sortedIssues, safePage])

  const showingFrom = sortedIssues.length > 0 ? (safePage - 1) * ITEMS_PER_PAGE + 1 : 0
  const showingTo = Math.min(safePage * ITEMS_PER_PAGE, sortedIssues.length)

  // Reset page to 1 when filters/search/sort change
  const handleFilterChange = (newFilter: IssueFilter) => {
    setFilter(newFilter)
    setCurrentPage(1)
  }

  const handleBrokerFilterChange = (value: string) => {
    setBrokerFilter(value)
    setCurrentPage(1)
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleSort = (field: SortField) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { field, direction: 'desc' }
    })
    setCurrentPage(1)
  }

  const renderSortIndicator = (field: SortField) => {
    if (sortConfig.field !== field) return null
    return (
      <span className="ml-1 inline-block text-primary-500">
        {sortConfig.direction === 'asc' ? '▲' : '▼'}
      </span>
    )
  }

  // Generate page numbers for pagination
  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const pages: (number | '...')[] = [1]

    if (safePage > 3) pages.push('...')

    const start = Math.max(2, safePage - 1)
    const end = Math.min(totalPages - 1, safePage + 1)

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (safePage < totalPages - 2) pages.push('...')

    pages.push(totalPages)
    return pages
  }

  const toggleExpand = (rowKey: string) => {
    setExpandedRow(expandedRow === rowKey ? null : rowKey)
  }

  // Modal content
  const getModalConfig = () => {
    if (!modalAction) return null

    switch (modalAction.type) {
      case 'mark_sold_out':
        return {
          title: 'Mark all units as Sold?',
          message: `This project still has units that are Available or Reserved. If you continue, all remaining units for "${modalAction.projectName}" will be updated to Sold. This action cannot be easily undone.`,
          confirmLabel: 'Confirm & Update Units',
          confirmVariant: 'danger' as const,
        }
      case 'confirm_sold':
        return {
          title: 'Confirm Ghost Units as Sold',
          message: `These ${modalAction.ghostUnitCount ?? 0} units for "${modalAction.projectName}" have not been seen in recent scrapes. They will be marked as sold.`,
          confirmLabel: 'Confirm & Update Units',
          confirmVariant: 'danger' as const,
        }
      default:
        return null
    }
  }

  const modalConfig = getModalConfig()

  if (isLoading) {
    return (
      <div className="card">
        <div className="h-6 w-64 animate-pulse bg-secondary-200 rounded mb-6" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 animate-pulse bg-secondary-100 rounded" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card pb-20">
        {/* Search Input */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-secondary-200 rounded-lg bg-white text-secondary-900 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-secondary-900">
              {filterCounts.all} project{filterCounts.all !== 1 ? 's' : ''} need{filterCounts.all === 1 ? 's' : ''} attention
            </h3>
          </div>

          {/* Filter Tabs + Broker Dropdown */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center space-x-1 bg-secondary-100 rounded-lg p-1">
              {[
                { key: 'all' as IssueFilter, label: 'All' },
                { key: 'scraper_error' as IssueFilter, label: 'Errors' },
                { key: 'stale' as IssueFilter, label: 'Stale' },
                { key: 'ghost_units' as IssueFilter, label: 'Ghosts' },
                { key: 'delisted_at_source' as IssueFilter, label: 'Delisted' },
                { key: 'relisted' as IssueFilter, label: 'Relisted' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleFilterChange(tab.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    filter === tab.key
                      ? 'bg-white text-secondary-900 shadow-sm'
                      : 'text-secondary-500 hover:text-secondary-700'
                  }`}
                >
                  {tab.label}
                  {filterCounts[tab.key] > 0 && (
                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                      filter === tab.key ? 'bg-primary-100 text-primary-700' : 'bg-secondary-200 text-secondary-600'
                    }`}>
                      {filterCounts[tab.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Broker Filter */}
            <div className="flex items-center space-x-1.5">
              <label htmlFor="broker-filter" className="text-sm font-medium text-secondary-500">
                Broker:
              </label>
              <select
                id="broker-filter"
                value={brokerFilter}
                onChange={(e) => handleBrokerFilterChange(e.target.value)}
                className="text-sm border border-secondary-200 rounded-md px-2 py-1.5 bg-white text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Brokers</option>
                {brokerNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table (desktop) + Cards (mobile) */}
        {paginatedIssues.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {paginatedIssues.map((issue) => {
                const typeConfig = issueTypeConfig[issue.issue_type]
                return (
                  <div
                    key={`mobile-${issue.project_id}-${issue.issue_type}`}
                    className="border border-secondary-200 rounded-lg p-4 bg-white space-y-3"
                  >
                    {/* Card Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/projects/${issue.project_id}`}
                            className="text-sm font-semibold text-primary-600 hover:text-primary-800 hover:underline truncate"
                          >
                            {issue.project_name}
                          </Link>
                          <a
                            href={issue.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary-400 hover:text-primary-600 flex-shrink-0"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                        <p className="text-xs text-secondary-500 mt-0.5">{issue.broker_name}</p>
                      </div>
                      <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full border flex-shrink-0 ${typeConfig.color}`}>
                        <typeConfig.icon className="h-3 w-3 mr-1" />
                        {typeConfig.label}
                      </span>
                    </div>

                    {/* Card Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-secondary-50 rounded p-2">
                        <p className={`text-sm font-bold ${
                          issue.days_since_success > 7 ? 'text-red-600' :
                          issue.days_since_success > 3 ? 'text-amber-600' : 'text-secondary-900'
                        }`}>
                          {issue.days_since_success}d
                        </p>
                        <p className="text-xs text-secondary-400">Stale</p>
                      </div>
                      <div className="bg-secondary-50 rounded p-2">
                        <p className="text-sm font-bold text-secondary-900">{issue.available_units}</p>
                        <p className="text-xs text-secondary-400">At Risk</p>
                      </div>
                      <div className="bg-secondary-50 rounded p-2">
                        <p className="text-sm font-bold text-secondary-900">{Math.round(issue.completion_rate)}%</p>
                        <p className="text-xs text-secondary-400">Sold</p>
                      </div>
                    </div>

                    {/* Recommendation */}
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${confidenceColors[issue.recommendation.confidence]}`}>
                        {issue.recommendation.confidence}
                      </span>
                      <span className="text-sm text-secondary-700">{humanizeAction(issue.recommendation.action)}</span>
                    </div>

                    {/* Card Actions */}
                    <div className="flex items-center justify-between pt-1 border-t border-secondary-100">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleAction({ type: 'dismiss', projectId: issue.project_id, projectName: issue.project_name })}
                          className="text-xs px-3 py-1.5 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                        >
                          Dismiss
                        </button>
                        <button
                          onClick={() => handleAction({ type: 'rescrape', projectId: issue.project_id, projectName: issue.project_name })}
                          className="text-xs px-2 py-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                          title="Trigger re-scrape"
                        >
                          <RefreshCw className="h-3 w-3" />
                        </button>
                      </div>
                      {issue.issue_type === 'ghost_units' ? (
                        <button
                          onClick={() => handleAction({ type: 'confirm_sold', projectId: issue.project_id, projectName: issue.project_name, ghostUnitCount: issue.ghost_unit_count, recommendedSoldDate: issue.recommended_sold_date, soldDateReasoning: issue.sold_date_reasoning })}
                          className="text-xs px-2 py-1.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                        >
                          Confirm Sold
                        </button>
                      ) : issue.issue_type === 'relisted' ? (
                        <Link
                          to={`/projects/${issue.project_id}`}
                          className="text-xs px-2 py-1.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        >
                          Open project
                        </Link>
                      ) : (
                        <button
                          onClick={() => handleAction({ type: 'mark_sold_out', projectId: issue.project_id, projectName: issue.project_name, availableUnits: issue.available_units, totalUnits: issue.total_units, soldUnits: issue.sold_units, reservedUnits: issue.reserved_units, recommendedSoldDate: issue.recommended_sold_date, soldDateReasoning: issue.sold_date_reasoning })}
                          className="text-xs px-2 py-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors border border-red-200"
                        >
                          Mark Sold
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50 sticky top-0 z-10 shadow-[0_2px_4px_rgba(0,0,0,0.05)]">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none hover:text-secondary-700"
                      onClick={() => handleSort('project_name')}
                    >
                      Project{renderSortIndicator('project_name')}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none hover:text-secondary-700"
                      onClick={() => handleSort('issue_type')}
                    >
                      Issue{renderSortIndicator('issue_type')}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none hover:text-secondary-700"
                      onClick={() => handleSort('recommendation.confidence')}
                    >
                      Recommendation{renderSortIndicator('recommendation.confidence')}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none hover:text-secondary-700"
                      onClick={() => handleSort('completion_rate')}
                    >
                      Completion{renderSortIndicator('completion_rate')}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none hover:text-secondary-700"
                      onClick={() => handleSort('available_units')}
                    >
                      Units at Risk{renderSortIndicator('available_units')}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer select-none hover:text-secondary-700"
                      onClick={() => handleSort('days_since_success')}
                    >
                      Days Stale{renderSortIndicator('days_since_success')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {paginatedIssues.map((issue) => {
                    const typeConfig = issueTypeConfig[issue.issue_type]
                    const isExpanded = expandedRow === `${issue.project_id}-${issue.issue_type}`

                    return (
                      <React.Fragment key={`${issue.project_id}-${issue.issue_type}`}>
                        <tr
                          className="hover:bg-secondary-50 cursor-pointer transition-colors"
                          onClick={() => toggleExpand(`${issue.project_id}-${issue.issue_type}`)}
                        >
                          {/* Project */}
                          <td className="px-4 py-4">
                            <div>
                              <div className="flex items-center gap-1.5">
                                <Link
                                  to={`/projects/${issue.project_id}`}
                                  className="text-sm font-semibold text-primary-600 hover:text-primary-800 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {issue.project_name}
                                </Link>
                                <a
                                  href={issue.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-secondary-400 hover:text-primary-600 flex-shrink-0"
                                  onClick={(e) => e.stopPropagation()}
                                  title={issue.website}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs font-medium text-secondary-500">{issue.broker_name}</span>
                                <span className="text-xs text-secondary-300">ID: {issue.project_id}</span>
                              </div>
                            </div>
                          </td>

                          {/* Issue Type */}
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${typeConfig.color}`}>
                              <typeConfig.icon className="h-3 w-3 mr-1" />
                              {typeConfig.label}
                              {issue.issue_type === 'ghost_units' && issue.ghost_unit_count && (
                                <span className="ml-1">({issue.ghost_unit_count})</span>
                              )}
                            </span>
                            {issue.consecutive_errors > 0 && issue.issue_type === 'scraper_error' && (
                              <p className="text-xs text-red-500 mt-0.5">{issue.consecutive_errors}x consecutive</p>
                            )}
                          </td>

                          {/* Recommendation */}
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${confidenceColors[issue.recommendation.confidence]}`}>
                                {issue.recommendation.confidence}
                              </span>
                              <span className="text-sm text-secondary-700">
                                {humanizeAction(issue.recommendation.action)}
                              </span>
                            </div>
                          </td>

                          {/* Completion */}
                          <td className="px-4 py-4">
                            <div>
                              <div className="flex items-center space-x-2">
                                <div className="w-20 bg-secondary-200 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="bg-green-500 h-full transition-all duration-300"
                                    style={{ width: `${issue.completion_rate}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-secondary-700">
                                  {Math.round(issue.completion_rate)}%
                                </span>
                              </div>
                              <p className="text-xs text-secondary-400 mt-0.5">
                                {issue.sold_units} sold / {issue.total_units} total
                              </p>
                            </div>
                          </td>

                          {/* Units at Risk */}
                          <td className="px-4 py-4">
                            <div>
                              <span className="text-sm font-bold text-secondary-900">
                                {issue.available_units}
                              </span>
                              <span className="text-xs text-secondary-400 ml-1">
                                / {issue.total_units}
                              </span>
                              {issue.reserved_units > 0 && (
                                <p className="text-xs text-blue-500 mt-0.5">{issue.reserved_units} reserved</p>
                              )}
                            </div>
                          </td>

                          {/* Days Stale */}
                          <td className="px-4 py-4">
                            <span className={`text-sm font-medium ${
                              issue.days_since_success > 7 ? 'text-red-600' :
                              issue.days_since_success > 3 ? 'text-amber-600' : 'text-secondary-600'
                            }`}>
                              {issue.days_since_success}d
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleAction({
                                    type: 'dismiss',
                                    projectId: issue.project_id,
                                    projectName: issue.project_name,
                                  })}
                                  className="text-xs px-3 py-1.5 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                                  title="Dismiss issue for 30 days"
                                >
                                  Dismiss
                                </button>
                                <button
                                  onClick={() => handleAction({
                                    type: 'rescrape',
                                    projectId: issue.project_id,
                                    projectName: issue.project_name,
                                  })}
                                  className="text-xs px-2 py-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                                  title="Trigger re-scrape"
                                >
                                  <RefreshCw className="h-3 w-3" />
                                </button>
                              </div>
                              <div className="w-px h-5 bg-secondary-200" />
                              {issue.issue_type === 'ghost_units' ? (
                                <button
                                  onClick={() => handleAction({
                                    type: 'confirm_sold',
                                    projectId: issue.project_id,
                                    projectName: issue.project_name,
                                    ghostUnitCount: issue.ghost_unit_count,
                                    recommendedSoldDate: issue.recommended_sold_date,
                                    soldDateReasoning: issue.sold_date_reasoning,
                                  })}
                                  className="text-xs px-2 py-1.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                                  title="Confirm ghost units as sold"
                                >
                                  Confirm Sold
                                </button>
                              ) : issue.issue_type === 'relisted' ? (
                                <Link
                                  to={`/projects/${issue.project_id}`}
                                  className="text-xs px-2 py-1.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                  title="Open the project to review the sold-out flag"
                                >
                                  Open project
                                </Link>
                              ) : (
                                <button
                                  onClick={() => handleAction({
                                    type: 'mark_sold_out',
                                    projectId: issue.project_id,
                                    projectName: issue.project_name,
                                    availableUnits: issue.available_units,
                                    totalUnits: issue.total_units,
                                    soldUnits: issue.sold_units,
                                    reservedUnits: issue.reserved_units,
                                    recommendedSoldDate: issue.recommended_sold_date,
                                    soldDateReasoning: issue.sold_date_reasoning,
                                  })}
                                  className="text-xs px-2 py-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors border border-red-200"
                                  title="Mark all available units as sold"
                                >
                                  Mark Sold
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="px-4 py-4 bg-secondary-50">
                              {/* Quick Links Bar */}
                              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-secondary-200">
                                <span className="text-xs font-medium text-secondary-400 uppercase tracking-wider mr-1">Quick Links:</span>
                                <Link
                                  to={`/projects/${issue.project_id}`}
                                  target="_blank"
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200 transition-colors"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  View Project
                                </Link>
                                <a
                                  href={issue.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-secondary-50 text-secondary-700 hover:bg-secondary-100 border border-secondary-200 transition-colors"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  Source Website
                                </a>
                                <a
                                  href={`https://web.archive.org/web/*/${issue.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors"
                                  title="View archived snapshots on Wayback Machine"
                                >
                                  <History className="h-3 w-3" />
                                  Wayback Machine
                                </a>
                                <a
                                  href={`https://archive.ph/newest/${issue.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors"
                                  title="View archived snapshots on archive.today"
                                >
                                  <History className="h-3 w-3" />
                                  archive.today
                                </a>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                {/* Error Details */}
                                {issue.error_message && (
                                  <div className="md:col-span-3">
                                    <p className="text-secondary-500 font-medium mb-1">Error Message</p>
                                    <div className="bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm whitespace-pre-wrap">
                                      {issue.error_message}
                                    </div>
                                    {issue.error_classification && (
                                      <p className="mt-1 text-xs text-secondary-400">
                                        Classification: <span className="font-medium text-secondary-600">{issue.error_classification}</span>
                                      </p>
                                    )}
                                  </div>
                                )}

                                {/* Recommendation */}
                                <div>
                                  <p className="text-secondary-500 font-medium mb-1">Recommendation</p>
                                  <div className="bg-white border border-secondary-200 rounded p-3">
                                    <p className="text-sm text-secondary-700">{issue.recommendation.reasoning}</p>
                                  </div>
                                </div>

                                {/* Unit Breakdown */}
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-secondary-500 font-medium">Unit Breakdown</p>
                                    <Link
                                      to={`/projects/${issue.project_id}`}
                                      target="_blank"
                                      className="text-xs text-primary-600 hover:text-primary-800 hover:underline"
                                    >
                                      View all units
                                    </Link>
                                  </div>
                                  <div className="bg-white border border-secondary-200 rounded p-3 space-y-1 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-secondary-500">Total:</span>
                                      <span className="font-medium text-secondary-900">{issue.total_units}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-secondary-500">Available:</span>
                                      <span className="font-medium text-secondary-900">{issue.available_units}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-secondary-500">Sold:</span>
                                      <span className="font-medium text-green-600">{issue.sold_units}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-secondary-500">Reserved:</span>
                                      <span className="font-medium text-blue-600">{issue.reserved_units}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Timeline */}
                                <div>
                                  <p className="text-secondary-500 font-medium mb-1">Timeline</p>
                                  <div className="bg-white border border-secondary-200 rounded p-3 space-y-1 text-sm">
                                    {issue.first_error_at && (
                                      <div className="flex justify-between">
                                        <span className="text-secondary-500">First error:</span>
                                        <span className="font-medium text-secondary-900">
                                          {new Date(issue.first_error_at).toLocaleDateString('pt-PT')}
                                        </span>
                                      </div>
                                    )}
                                    {issue.last_successful_scrape && (
                                      <div className="flex justify-between">
                                        <span className="text-secondary-500">Last success:</span>
                                        <span className="font-medium text-secondary-900">
                                          {new Date(issue.last_successful_scrape).toLocaleDateString('pt-PT')}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex justify-between">
                                      <span className="text-secondary-500">Consecutive errors:</span>
                                      <span className="font-medium text-red-600">{issue.consecutive_errors}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Ghost Units List */}
                                {issue.ghost_units && issue.ghost_units.length > 0 && (
                                  <div className="md:col-span-3">
                                    <p className="text-secondary-500 font-medium mb-2">
                                      Ghost Units ({issue.ghost_unit_count ?? issue.ghost_units.length})
                                      {issue.days_until_auto_cleanup != null && (
                                        <span className="ml-2 text-xs text-amber-600 font-normal">
                                          Auto-cleanup in {issue.days_until_auto_cleanup} days
                                        </span>
                                      )}
                                    </p>
                                    <div className="bg-white border border-secondary-200 rounded overflow-hidden">
                                      <table className="min-w-full divide-y divide-secondary-200 text-sm">
                                        <thead className="bg-secondary-50 sticky top-0 z-10">
                                          <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500">Ref</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500">Typology</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500">Price</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500">Last Seen</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500">Days Not Seen</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-secondary-500">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-secondary-100">
                                          {issue.ghost_units.map((ghost) => (
                                            <tr key={ghost.unit_id} className="hover:bg-secondary-50 transition-colors">
                                              <td className="px-3 py-2">
                                                <Link
                                                  to={`/units/${ghost.unit_id}`}
                                                  target="_blank"
                                                  className="font-medium text-primary-600 hover:text-primary-800 hover:underline inline-flex items-center gap-1"
                                                >
                                                  {ghost.unit_ref}
                                                  <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                                                </Link>
                                              </td>
                                              <td className="px-3 py-2 text-secondary-600">
                                                {ghost.typology || '-'}
                                              </td>
                                              <td className="px-3 py-2 text-secondary-600">
                                                {ghost.current_price ? `${Number(ghost.current_price).toLocaleString('pt-PT')} \u20AC` : '-'}
                                              </td>
                                              <td className="px-3 py-2 text-secondary-600">
                                                {ghost.last_scraped_at ? new Date(ghost.last_scraped_at).toLocaleDateString('pt-PT') : '-'}
                                              </td>
                                              <td className="px-3 py-2">
                                                <span className={`font-medium ${
                                                  ghost.days_not_seen > 10 ? 'text-red-600' : 'text-amber-600'
                                                }`}>
                                                  {ghost.days_not_seen}d
                                                </span>
                                              </td>
                                              <td className="px-3 py-2 text-secondary-500">{ghost.status}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No issues found</h3>
            <p className="text-secondary-500">
              {filter !== 'all' || brokerFilter !== 'all' || searchQuery.trim()
                ? 'No issues match the current filters. Try adjusting your search or filters.'
                : 'All projects are running smoothly.'}
            </p>
          </div>
        )}

        {/* Pagination */}
        {sortedIssues.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-secondary-200">
            <p className="text-sm text-secondary-500">
              Showing {showingFrom}-{showingTo} of {sortedIssues.length}
            </p>

            {totalPages > 1 && (
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-secondary-200 text-secondary-600 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-secondary-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        page === safePage
                          ? 'bg-primary-600 text-white'
                          : 'border border-secondary-200 text-secondary-600 hover:bg-secondary-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-secondary-200 text-secondary-600 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {modalConfig && (
        <ConfirmationModal
          isOpen={!!modalAction}
          onClose={() => { setModalAction(null); setSoldDate(''); }}
          onConfirm={confirmAction}
          title={modalConfig.title}
          message={modalConfig.message}
          confirmLabel={modalConfig.confirmLabel}
          confirmVariant={modalConfig.confirmVariant}
          isLoading={resolveMutation.isLoading}
          requireConfirmText={modalAction?.type === 'mark_sold_out' ? 'SOLD OUT' : undefined}
        >
          {modalAction?.type === 'mark_sold_out' && (
            <div className="space-y-3">
              {/* Unit Breakdown */}
              <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm text-secondary-700">
                  <span>Total units</span>
                  <span className="font-semibold text-secondary-900">{modalAction.totalUnits ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm text-secondary-700">
                  <span>Units already Sold</span>
                  <span className="font-semibold text-secondary-900">{modalAction.soldUnits ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm text-secondary-700">
                  <span>Units to update</span>
                  <span className="font-semibold text-secondary-900">{(modalAction.availableUnits ?? 0) + (modalAction.reservedUnits ?? 0)}</span>
                </div>
                {((modalAction.availableUnits ?? 0) > 0 || (modalAction.reservedUnits ?? 0) > 0) && (
                  <div className="pt-2 border-t border-secondary-200">
                    <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-1">Breakdown</p>
                    <div className="space-y-1">
                      {(modalAction.availableUnits ?? 0) > 0 && (
                        <div className="flex justify-between text-sm text-secondary-600">
                          <span>Available</span>
                          <span className="font-medium text-secondary-900">{modalAction.availableUnits}</span>
                        </div>
                      )}
                      {(modalAction.reservedUnits ?? 0) > 0 && (
                        <div className="flex justify-between text-sm text-secondary-600">
                          <span>Reserved</span>
                          <span className="font-medium text-secondary-900">{modalAction.reservedUnits}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sold Date Picker */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Sales Date</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-white text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  value={soldDate}
                  onChange={(e) => setSoldDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
                {modalAction?.soldDateReasoning && (
                  <p className="mt-1.5 text-xs text-secondary-500">
                    {modalAction.soldDateReasoning}
                  </p>
                )}
              </div>
            </div>
          )}
          {modalAction?.type === 'confirm_sold' && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-2">
              <p className="text-sm text-purple-800">
                Each unit will be marked as sold using its own <span className="font-semibold">last seen date</span> as the sales date, since units may have disappeared at different times.
              </p>
              {modalAction?.soldDateReasoning && (
                <p className="text-xs text-purple-600">
                  {modalAction.soldDateReasoning}
                </p>
              )}
            </div>
          )}
        </ConfirmationModal>
      )}
    </>
  )
}
